import { getStore } from '../store/get-store';
import {
  initialEntityScopeState,
  rootMovedSince,
  type EntityScopeState,
} from '../slice/entityScopeSlice';
import { scopeRootOfStoredToken } from '../util/jwt';

/**
 * The non-React face of the entity-scope source (DV-3013 spec §3.1).
 *
 * A plain function, not a hook, for the same reason `switchEntity` is: the auth
 * bootstrap, the RTK Query base query and the axios interceptor all need the
 * scope outside a component. Same store as `useEntityScope`, so the two cannot
 * drift.
 *
 * Falls back to the initial state rather than throwing when the store has not
 * registered yet: this runs on the pre-render path, and an unscoped answer is
 * both correct and safe there, whereas a throw would take down the bootstrap.
 */
export const getEntityScope = (): EntityScopeState => {
  try {
    return (getStore().getState() as { entityScope: EntityScopeState }).entityScope;
  } catch {
    return initialEntityScopeState;
  }
};

/**
 * Is this tab's selection KNOWN to be out of the stored token's reach — its
 * stamp says unreachable, or the token's root has moved off it? DV-3421.
 *
 * <p>Then re-choosing the SAME account is a real request (it re-anchors the
 * token back to it), not a no-op to short-circuit. A scope with no stamp is
 * not known to be anything — the backend check may be in flight or have failed
 * — so it stays a no-op (review of 0a17c730, item 6).
 */
export const isSelectionKnownUnreachable = (): boolean =>
  rootMovedSince(scopeRootOfStoredToken(), getEntityScope());
